// BookedEvents.jsx
import React, { useEffect, useRef, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import BookedEventCard from "./BookedEventCard";
import "./BookedEvent.css";
import { Box, Typography, CircularProgress } from "@mui/material";
import EventPass from "../../../../Assets/css/Community/EventPass.webp";
import { getRegisteredAttendeesHistory } from "../../../../Redux/Reducers/communityReducer";
import usePaginateBookedEvent from "../Feeds/usePaginateBookedEvent";


const BookedEvents = () => {
  const dispatch = useDispatch();
  const [showHistory, setShowHistory] = useState(true);
  const [loaderNode, setLoaderNode] = useState(null);
  const loaderRef = useRef();

  const registeredAttendees = useSelector(
    (state) => state?.community?.registeredAttendees
  );

  // Memoize fetchParams to prevent unnecessary recreations
  const fetchParams = useMemo(() => ({
    pastEvents: true
  }), []);

  // Use pagination hook for history events
  const {
    data: historyEvents,
    loading: loadingHistory,
    hasMore: hasMoreHistory,
    handleLoadMore: handleLoadMoreHistory,
    handleRefresh: handleRefreshHistory,
  } = usePaginateBookedEvent(
    (state) => state?.community?.registeredAttendeesHistory,
    getRegisteredAttendeesHistory,
    fetchParams
  );

  // Function to sort events by date (newest first)
  const sortEvents = (events) => {
    return [...events].sort(
      (a, b) => new Date(b.start_time) - new Date(a.start_time)
    );
  };

  // Current events (sorted)
  const currentEvents = sortEvents(registeredAttendees);

  // Intersection Observer for infinite scroll
useEffect(() => {
    if (!showHistory || !hasMoreHistory || loadingHistory || !loaderNode) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingHistory) {
          handleLoadMoreHistory();
        }
      },
      { rootMargin: "200px" }
    );
    
    observer.observe(loaderNode);
    
    return () => {
      observer.disconnect(); // Proper cleanup
    };
  }, [hasMoreHistory, loadingHistory, handleLoadMoreHistory, showHistory, loaderNode]);

  // Refresh history when component mounts
  useEffect(() => {
    handleRefreshHistory();
  }, []);

  // Check if both current and history events are empty
  const bothEmpty = currentEvents?.length === 0 && historyEvents?.length === 0;

  // Empty state component
  const renderEmptyState = (isHistory) => (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      color: "#555",
      py: 4
    }}>
      <img
        src={EventPass}
        alt="No Events"
        style={{ width: "80px", marginBottom: "16px", opacity: 0.8 }}
      />
      <Typography variant="h6">
        {isHistory ? "No Past Events" : "No Event Pass"}
      </Typography>
    </Box>
  );

  return (
    <div className="booked-events">
      {/* Current Events Section */}
      {currentEvents.length === 0 ? (
        bothEmpty ? renderEmptyState(false) : null
      ) : (
        currentEvents.map((event) => (
          <Box key={`current-${event.id}`} mb={2}>
            <BookedEventCard event={event} />
          </Box>
        ))
      )}

      {/* Past Events Section - Only show if not both empty */}
      {!bothEmpty && (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Past Events
            </Typography>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold', 
                fontSize: "medium", 
                color: "#0044C6",
                cursor: "pointer"
              }}  
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? "Hide" : "Show"}
            </Typography>
          </Box>
          
          {showHistory && (
            <Box mt={1}>
              {historyEvents?.length === 0 && !loadingHistory ? (
                renderEmptyState(true)
              ) : (
                <>
                  {historyEvents?.map((event) => (
                    <Box key={`past-${event.id}`} mb={2}>
                      <BookedEventCard event={event} isPastEvent={true} />
                    </Box>
                  ))}
                  
                  {/* Loader and end message */}
                  {loadingHistory && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  
                  {!loadingHistory && !hasMoreHistory && historyEvents.length > 0 && (
                    <Typography variant="body2" sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                      You've reached the end of past events
                    </Typography>
                  )} 
                  
                  {/* End of results message */}
                  {/* {!hasMoreHistory && historyEvents.length > 0 && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        textAlign: 'center', 
                        py: 2,
                        color: 'text.secondary'
                      }}
                    >
                      You've reached the end of past events
                    </Typography>
                  )} */}
                  
                  {/* Loader ref for intersection observer */}
                  {hasMoreHistory && !loadingHistory && (
                    <div 
                      ref={setLoaderNode} // Callback ref
                      style={{ height: "10px", background: "transparent" }}
                    />
                  )}
                </>
              )}
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default BookedEvents;